<template>
  <div class="card order-table">

    <header class="card-header">

        <div class="card-header-title has-text-dark is-size-4">
            <p>{{ pagination.total }} ORDERS</p>
        </div>

        <div class="buttons">

            <slot name="headerButtons" />

        </div>

    </header>

    <div class="card-content" ref="cardContent">
        <base-table
          :service="$options.orderService"
          fetch-func="all"
          :fetch-params="params"
          :columns="columns"
          default-sort-field="releaseDate"
          default-sort-direction="desc"
          :per-page="perPage"
          :selectable="true"
          :selected.sync="selected_"
          checkable
          :checked.sync="checked_"
          :is-row-checkable="isRowCheckable"
          sticky
          :height="height"
          infinite-scroll
          :card-layout="cardLayout"
          @click="onClick"
          @fetch="onTableFetch"
          @paginate="onTablePaginate"
          ref="ordersTable"
          v-if="source === 'inbound'"
        >

          <template #rowId="{ row }">
            {{ row.orderId || row.id }}
          </template>

          <template #rowDate="{ row }">
            {{ row.releaseDate | toLocalDateAndTime | uppercase  }}
          </template>

          <template #rowAssignedTo="{ row }">
            <avatar-initials :unassigned="!row.assignedTo" :full-name="row.assignedTo ? row.assignedTo.fullName : ''"></avatar-initials>
          </template>

          <template #rowDescription="{ row }">
            {{ `${row.priority ? row.priority.split(" ").slice(-1) : null}` + ' - ' + `${row.orderSubtypeCode ? row.orderSubtypeCode.name : null}` + ' - ' + row.address + ' - ' + row.workDescription }}
          </template>

          <template #rowClaimLines="{ row }">
            {{ row.claimLinesTotal | numberPriceFormatter }}
          </template>

          <template #rowStatus="{ row }">
            {{ row.status | statusDisplay }}
          </template>

        </base-table>

        <base-table
          :service="$options.orderService"
          fetch-func="all"
          :fetch-params="params"
          :columns="columns"
          default-sort-direction="desc"
          :per-page="perPage"
          :selectable="true"
          :selected.sync="selected_"
          checkable
          :checked.sync="checked_"
          :is-row-checkable="isRowCheckable"
          sticky
          :height="height"
          infinite-scroll
          :card-layout="cardLayout"
          @click="onClick"
          @fetch="onTableFetch"
          @paginate="onTablePaginate"
          ref="ordersTable"
          v-if="source === 'outbound'"
        >

          <template #rowId="{ row }">
            {{ row.orderId || row.id }}
          </template>

          <template #rowDate="{ row }">
            {{ row.releaseDate | toLocalDateAndTime | uppercase  }}
          </template>

          <template #rowAssignedTo="{ row }">
            <avatar-initials :unassigned="!row.assignedTo" :full-name="row.assignedTo ? row.assignedTo.fullName : ''"></avatar-initials>
          </template>

          <template #rowDescription="{ row }">
            {{ `${row.priority ? row.priority.split(" ").slice(-1) : null}` + ' - ' + `${row.orderSubtypeCode ? row.orderSubtypeCode.name : null}` + ' - ' + row.address + ' - ' + row.workDescription }}
          </template>

          <template #rowClaimLines="{ row }">
            {{ row.claimLinesTotal | numberPriceFormatter }}
          </template>

          <template #rowStatus="{ row }">
            {{ row.status | statusDisplay }}
          </template>

        </base-table>

    </div>

  </div>
</template>

<script>

    import BaseTable from "@/components/partials/BaseTable";
    import { OrderService } from "@/services/order.service.js";
    import AvatarInitials from "@/components/AvatarInitials.vue";

    export default {

      /**
       * Non reactive data here
       */
      orderService: OrderService,
      columns: [
        {
          field: "orderId",
          label: "Number",
          sortable: true,
          meta: {
            rowSlot: "rowId"
          }
        },
        {
          field: "releaseDate",
          label: "Date",
          sortable: true,
          meta:{
            rowSlot: 'rowDate',
          }
        },
        // NOTE: We'll eject the assignedTo column unless
        // the user is from the currently filtered firm.
        // (we will use a computed "columns" property)
        {
          field: "assignedTo",
          label: "", // no label?
          sortable: false,
          meta: {
            rowSlot: 'rowAssignedTo',
          }
        },
        {
          field: "description",
          label: "Details",
          sortable: true,
          meta: {
            expanded: true,
            rowSlot: "rowDescription"
          }
        },
        {
          field: "claimLinesTotal",
          label: "Value ($)",
          sortable: true,
          meta:{
            rowSlot: 'rowClaimLines',
            class: "has-text-right no-wrap",
          }
        },
        {
          field: "status",
          label: "Status",
          sortable: true,
          meta: {
            rowSlot: "rowStatus"
          }
        }
      ],
      // End non reactive data here
      props: {
        fetchParams: {
          type: Object,
          default: () => {}
        },
        filters: {
          type: Object,
          default: () => {}
        },
        selected: {
          type: Object,
          default: () => {}
        },
        checked: {
          type: Array,
          default: () => []
        },
        source: {
          type: String,
          required: true,
          validator: value => ["inbound", "outbound"].indexOf(value) !== -1
        },
        perPage: {
          type: Number,
          default: 100
        },
        cardLayout: {
          type: Boolean,
          default: false
        }
      },
      components: {
        BaseTable,
        AvatarInitials
      },
      data: () => ({
        orders: [],
        selected_: null,
        checked_: [],
        pagination: {},
        height: 300,
        show_assigned_to: false,
      }),
      computed: {
        isPendingClaimApprovalChecked() {
          return !!this.fetchParams.pending_approval;
        },
        params() {
          let additionalParams = {
            includes: "claimLinesTotal,assignedTo,orderSubtypeCode"
          };

          // If Pending Claim Approval filter is checked
          if (this.isPendingClaimApprovalChecked) {
            additionalParams.includes = additionalParams.includes + ",state";
            additionalParams.state_only = ["can_be_blap_by_user"];
          }

          return {
            ...additionalParams,
            ...this.fetchParams
          };
        },
        columns() {
          let columns = this.$options.columns;
          // Only show the `assigned_to` avatar column when the `issued_to` filter is set to `auth_user->firm_id`
          if ( ! this.show_assigned_to) {
            columns = columns.filter(col => {
              return col.field !== 'assignedTo';
            });
          }
          return columns;
        }
      },
      mounted() {
        const cardContent = this.$refs.cardContent;
        this.height = cardContent.clientHeight;
      },
      watch: {
        selected(val) {
          this.selected_ = val;
        },
        selected_(val) {
          this.$emit("update:selected", val);
        },
        checked(val) {
          this.checked_ = val;
        },
        checked_(val) {
          this.$emit("update:checked", val);
        }
      },
      methods: {
        fetch() {
          return this.$refs.ordersTable.fetchData().then(() => {
            const disabled = document.getElementsByClassName(
              "b-checkbox checkbox is-disabled"
            );

            for (let x = 0; x < disabled.length; x++) {
              disabled[x].setAttribute("title", "No Authority to Approve.");
            }
          });
        },
        isRowCheckable(row) {
          if (this.isPendingClaimApprovalChecked) {
            return (
              row.state &&
              row.state.state &&
              row.state.state.can_be_blap_by_user &&
              row.state.state.can_be_blap_by_user.value
            );
          }

          return true;
        },
        onClick(row) {
          // Redirect to edit form
          if ((row.status || "").toLowerCase() == "draft") {
            this.$router.push({ name: "order-update", params: { id: row.id }});
            return;
          }

          let params = `id=${row.id}`;

          if (row.orderId) {
            params = `orderId=${row.orderId}`;
          }

          window.history.replaceState(null, null, `/orders?${params}`);

          if (this.selected_ && this.selected_.id === row.id) {
            this.selected_ = null;
          }
        },
        onTableFetch(data) {
          this.$emit("fetch", data);
        },
        onTablePaginate(pagination) {
          this.pagination = pagination;
        }
      }
    };
</script>

<style lang="scss" scoped>
    .card-header-tag {
        margin-left: 8px;
        .tag {
            margin-bottom: 0;
            margin-top: -0.5rem;
        }
    }
    .card-content {
        padding: 0;
    }
</style>


// WEBPACK FOOTER //
// src/components/pages/Orders/list-new/partials/table.vue